import config from '../config/config'
import { BgmExtension, BgmTrack } from '../enums/enums'

/** Placeholder storage uses one shared clip per MCQ option letter (A–D), not per answer UUID. */
export const DAX_EXPLANATION_OPTION_LETTERS = ['A', 'B', 'C', 'D'] as const
export type DaxExplanationOptionLetter = typeof DAX_EXPLANATION_OPTION_LETTERS[number]

/** Shared miss clips: why a false pick is wrong, then why the right answers are right. */
export const DAX_EXPLANATION_CLIP_WRONG = 'whyWrongIsWrong' as const
export const DAX_EXPLANATION_CLIP_CORRECT = 'whyCorrectIsCorrect' as const
export const DAX_EXPLANATION_CLIPS = [DAX_EXPLANATION_CLIP_WRONG, DAX_EXPLANATION_CLIP_CORRECT] as const
export type DaxExplanationClip = typeof DAX_EXPLANATION_CLIPS[number]

/** Caption files for the shared miss clips (names do not match the MP3 stems). */
export const DAX_EXPLANATION_SUBTITLE_FILE: Record<DaxExplanationClip, string> = {
  [DAX_EXPLANATION_CLIP_WRONG]: 'why-wrong-is-wrong.vtt',
  [DAX_EXPLANATION_CLIP_CORRECT]: 'why-the-correct-answer-is-correct.vtt'
}

type DaxExplanationAnswer = { id: string, is_correct: boolean }

export function getBgmUrl (trackName: string): string {
  const courseFilesUrl = config.loadEnvironmentVariables().courseFilesUrl
  const baseBgmUrl = `${courseFilesUrl}audio/bgm`
  const extension = getBgmExtension(trackName)

  const url = `${baseBgmUrl}/${trackName}${extension}`

  return url
}

export function getSfxUrl (sfxName: string): string {
  const courseFilesUrl = config.loadEnvironmentVariables().courseFilesUrl
  return `${courseFilesUrl}audio/sfx/${sfxName}.wav`
}

/**
 * Maps a 0-based answer index on an MCQ to option letter A–D.
 * Returns null when the index is outside the four-option placeholder set.
 */
export function getDaxExplanationOptionLetter (answerIndex: number): DaxExplanationOptionLetter | null {
  if (answerIndex < 0 || answerIndex >= DAX_EXPLANATION_OPTION_LETTERS.length) return null
  return DAX_EXPLANATION_OPTION_LETTERS[answerIndex]
}

/**
 * App-asset URL for a Dax explanation MP3
 * (`/assets/audio/dax/explanations/{clip}.mp3`). Miss playback uses `whyWrongIsWrong` /
 * `whyCorrectIsCorrect`. A–D letter names remain valid for the leftover option files.
 */
export function getDaxExplanationAudioUrl (clip: DaxExplanationClip | DaxExplanationOptionLetter | string): string {
  return `${process.env.PUBLIC_URL ?? ''}/assets/audio/dax/explanations/${clip}.mp3`
}

/**
 * App-asset URL for a Dax explanation VTT. Miss clips use `why-wrong-is-wrong.vtt` /
 * `why-the-correct-answer-is-correct.vtt`. A–D leftovers still use `{letter}.en.vtt`.
 */
export function getDaxExplanationSubtitleUrl (clip: DaxExplanationClip | DaxExplanationOptionLetter | string): string {
  const file = clip === DAX_EXPLANATION_CLIP_WRONG || clip === DAX_EXPLANATION_CLIP_CORRECT
    ? DAX_EXPLANATION_SUBTITLE_FILE[clip]
    : `${clip}.en.vtt`
  return `${process.env.PUBLIC_URL ?? ''}/assets/audio/dax/explanations/${file}`
}

/**
 * Selected answer IDs from Unity's `McqWrongAnswer` JSON (`{ question_id, answers: string[] }`).
 * Empty or invalid payloads yield no selection; the clip picker still uses the question's correct answers.
 */
export function parseMcqWrongAnswerSelection (json?: string): { questionId?: string, selectedAnswerIds: string[] } {
  if (json == null || json === '') return { selectedAnswerIds: [] }
  try {
    const parsed = JSON.parse(json) as { question_id?: unknown, answers?: unknown }
    const selectedAnswerIds = Array.isArray(parsed.answers)
      ? parsed.answers.filter((id): id is string => typeof id === 'string' && id.length > 0)
      : []
    const questionId = typeof parsed.question_id === 'string' && parsed.question_id.length > 0
      ? parsed.question_id
      : undefined
    return { questionId, selectedAnswerIds }
  } catch {
    return { selectedAnswerIds: [] }
  }
}

/**
 * Which shared miss clips to play. False pick → why that is wrong, then why the right answers
 * are right. Partial (some correct selected, none incorrect) → only why the others are also right.
 */
export function resolveDaxExplanationClips (
  answersInOrder: DaxExplanationAnswer[],
  selectedAnswerIds: string[]
): DaxExplanationClip[] {
  const selected = new Set(selectedAnswerIds)
  let hasFalsePick = false
  let hasMissedCorrect = false

  answersInOrder.forEach((answer) => {
    const isSelected = selected.has(answer.id)
    if (isSelected && !answer.is_correct) hasFalsePick = true
    if (!isSelected && answer.is_correct) hasMissedCorrect = true
  })

  if (hasFalsePick) return [DAX_EXPLANATION_CLIP_WRONG, DAX_EXPLANATION_CLIP_CORRECT]
  if (hasMissedCorrect) return [DAX_EXPLANATION_CLIP_CORRECT]
  return []
}

function findQuestionForDaxExplanation (
  questions: Array<{ id: string, answers: DaxExplanationAnswer[] }> | undefined,
  questionId: string | undefined,
  selectedAnswerIds: string[]
): { id: string, answers: DaxExplanationAnswer[] } | undefined {
  if (questions == null) return undefined
  return (questionId != null ? questions.find(q => q.id === questionId) : undefined)
    ?? (selectedAnswerIds.length > 0
      ? questions.find(q => q.answers.some(a => selectedAnswerIds.includes(a.id)))
      : undefined)
}

/**
 * Builds explanation VO URLs for a miss: `whyWrongIsWrong` and/or `whyCorrectIsCorrect`.
 */
export function resolveDaxExplanationAudioUrls (
  answersInOrder: DaxExplanationAnswer[],
  selectedAnswerIds: string[]
): string[] {
  return resolveDaxExplanationClips(answersInOrder, selectedAnswerIds).map(getDaxExplanationAudioUrl)
}

/**
 * Resolves Dax explanation VO for a miss: find the question, then the shared wrong/correct clips.
 */
export function resolveDaxExplanationAudioUrlsForQuestions (
  questions: Array<{ id: string, answers: DaxExplanationAnswer[] }> | undefined,
  questionId: string | undefined,
  selectedAnswerIds: string[]
): string[] {
  const question = findQuestionForDaxExplanation(questions, questionId, selectedAnswerIds)
  if (question == null) return []
  return resolveDaxExplanationAudioUrls(question.answers, selectedAnswerIds)
}

/**
 * Builds explanation subtitle URLs matching the miss-clip queue.
 */
export function resolveDaxExplanationSubtitleUrls (
  answersInOrder: DaxExplanationAnswer[],
  selectedAnswerIds: string[]
): string[] {
  return resolveDaxExplanationClips(answersInOrder, selectedAnswerIds).map(getDaxExplanationSubtitleUrl)
}

/**
 * Resolves Dax explanation VTTs for a miss: same clip queue as the MP3s.
 */
export function resolveDaxExplanationSubtitleUrlsForQuestions (
  questions: Array<{ id: string, answers: DaxExplanationAnswer[] }> | undefined,
  questionId: string | undefined,
  selectedAnswerIds: string[]
): string[] {
  const question = findQuestionForDaxExplanation(questions, questionId, selectedAnswerIds)
  if (question == null) return []
  return resolveDaxExplanationSubtitleUrls(question.answers, selectedAnswerIds)
}

export function getBgmExtension (trackName: string): string {
  switch (trackName) {
    case BgmTrack.None: return BgmExtension.None

    case BgmTrack.Dashboard: return BgmExtension.Dashboard
    case BgmTrack.Completed: return BgmExtension.Completed

    case BgmTrack.Cocaine : return BgmExtension.Cocaine
    case BgmTrack.Xanax : return BgmExtension.Xanax
    case BgmTrack.Mdma : return BgmExtension.Mdma
    case BgmTrack.Response : return BgmExtension.Response
    case BgmTrack.Lightning : return BgmExtension.Lightning

    default: return BgmExtension.None
  }
}
